// resources/js/admin/hooks/cms/usePages.ts

import { useState, useEffect, useCallback } from "react";

import useToken from "../auth/useToken";
import { Page, PageDuplicateData, PageFormData, Revision } from "../../../types";
import { pageService } from "../../../services/pageService";

export default function usePages(siteSlug: string) {
  const { token } = useToken();
  const [pages, setPages] = useState<Page[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 20,
  });
  const [filterStatus, setFilterStatus] = useState<string | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filterLocale, setFilterLocale] = useState<string | undefined>(undefined);

  // 🔓 PUBLIC - Pas de token nécessaire
  const fetchPageBySlug = useCallback(async (slug: string, locale?: string): Promise<Page | null> => {
    if (!siteSlug) return null;

    setLoading(true);
    setError(null);
    try {
      const page = await pageService.getBySlug(siteSlug, slug, locale);
      return page;
    } catch (err) {
      setError("Erreur lors du chargement de la page");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [siteSlug]);

  // 🆕 PUBLIC - Récupérer une page pour le rendu public
  const fetchPublicPage = useCallback(async (slug: string, locale?: string): Promise<Page | null> => {
    if (!siteSlug) return null;

    setLoading(true);
    setError(null);
    try {
      const page = await pageService.getPublicPage(siteSlug, slug, locale);
      return page;
    } catch (err) {
      setError("Erreur lors du chargement de la page publique");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [siteSlug]);

  // 🔒 PROTÉGÉ - Token requis
  const fetchPages = useCallback(async (page: number = 1, perPage: number = 20) => {
    if (!token) {
      setError("Vous devez être connecté");
      return;
    }

    if (!siteSlug) return;

    setLoading(true);
    setError(null);
    try {
      const response = await pageService.getAll(
        siteSlug, 
        token, 
        page, 
        perPage, 
        filterStatus, 
        searchTerm || undefined
      );
      setPages(response.data);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (err) {
      setError("Erreur lors du chargement des pages");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token, siteSlug, filterStatus, searchTerm]);

  // 🔒 PROTÉGÉ - Token requis
  const fetchPageById = useCallback(async (id: string): Promise<Page | null> => {
    if (!token) {
      setError("Vous devez être connecté");
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const page = await pageService.getById(token, id);
      return page;
    } catch (err) {
      setError("Erreur lors du chargement de la page");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const createPage = useCallback(async (data: PageFormData): Promise<Page | null> => {
    if (!token) {
      setError("Vous devez être connecté");
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const newPage = await pageService.create(token, data);
      setPages((prev) => [newPage, ...prev]);
      return newPage;
    } catch (err: any) {
      setError("Erreur lors de la création de la page");
      console.error(err.response);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const updatePage = useCallback(async (id: string, data: Partial<PageFormData>): Promise<Page | null> => {
    if (!token) {
      setError("Vous devez être connecté");
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const updatedPage = await pageService.update(token, id, data);
      setPages((prev) => prev.map((p) => (p.id === id ? updatedPage : p)));
      return updatedPage;
    } catch (err) {
      setError("Erreur lors de la mise à jour de la page");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const deletePage = useCallback(async (id: string): Promise<boolean> => {
    if (!token) {
      setError("Vous devez être connecté");
      return false;
    }

    setLoading(true);
    setError(null);
    try {
      await pageService.delete(token, id);
      setPages((prev) => prev.filter((p) => p.id !== id));
      return true;
    } catch (err) {
      setError("Erreur lors de la suppression de la page");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const restorePage = useCallback(async (id: string): Promise<Page | null> => {
    if (!token) {
      setError("Vous devez être connecté");
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const restoredPage = await pageService.restore(token, id);
      setPages((prev) => [...prev, restoredPage]);
      return restoredPage;
    } catch (err) {
      setError("Erreur lors de la restauration de la page");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const togglePublish = useCallback(async (id: string): Promise<Page | null> => {
    if (!token) {
      setError("Vous devez être connecté");
      return null;
    }

    setLoading(true);
    try {
      const updatedPage = await pageService.togglePublish(token, id);
      setPages((prev) => prev.map((p) => (p.id === id ? updatedPage : p)));
      return updatedPage;
    } catch (err) {
      setError("Erreur lors du changement de statut de publication");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const duplicatePage = useCallback(async (id: string, data: PageDuplicateData): Promise<Page | null> => {
    if (!token) {
      setError("Vous devez être connecté");
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const newPage = await pageService.duplicate(token, id, data);
      setPages((prev) => [newPage, ...prev]);
      return newPage;
    } catch (err) {
      setError("Erreur lors de la duplication de la page");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const fetchRevisions = useCallback(async (pageId: string, page: number = 1, 
    perPage: number = 20): Promise<Revision[] | null> => {
    if (!token) {
      setError("Vous devez être connecté");
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await pageService.getRevisions(token, pageId, page, perPage);
      return response.data;
    } catch (err) {
      setError("Erreur lors du chargement des révisions");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const restoreRevision = useCallback(async (pageId: string, revisionId: string): Promise<Page | null> => {
    if (!token) {
      setError("Vous devez être connecté");
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const restoredPage = await pageService.restoreRevision(token, pageId, revisionId);
      return restoredPage;
    } catch (err) {
      setError("Erreur lors de la restauration de la révision");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Filtres
  const filterByStatus = useCallback((status: string | undefined) => {
    setFilterStatus(status);
  }, []);

  const searchPages = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  useEffect(() => {
    if (token && siteSlug) {
      fetchPages();
    }
  }, [token, siteSlug, fetchPages]);

  return {
    pages,
    loading,
    error,
    pagination,
    filterStatus,
    searchTerm,
    fetchPages,
    fetchPageById,
    fetchPageBySlug,
    fetchPublicPage,  // 🆕
    createPage,
    updatePage,
    deletePage,
    restorePage,
    togglePublish,
    duplicatePage,
    fetchRevisions,
    restoreRevision,
    filterByStatus,
    searchPages,
  };
}